import {
  BarChartOutlined,
  BgColorsOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  FileSearchOutlined,
  HomeOutlined,
  MailOutlined,
  ShopOutlined,
  SyncOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { DASHBOARD_NAV, dashboardMenuIcon } from '@/config/dashboardNav';

export type PortalMenuAccess =
  | 'admin'
  | 'canViewBranchDailySales'
  | 'canViewHourlySales'
  | 'canViewBranchDailySalesCompare'
  | 'canViewBranchMonthlySales'
  | 'canViewYearlySalesComparison'
  | 'canViewFiscalWeeklySales'
  | 'canViewSalesBySku'
  | 'canViewSalesByPromotion'
  | 'canViewCouponUsage'
  | 'canViewVoucherUsage'
  | 'canViewPoDetails'
  | 'canViewGrnDetails'
  | 'canViewDoDetails'
  | 'canViewStockInventory'
  | 'canViewStockAging'
  | 'canViewStockMovement'
  | 'canViewBranchStockBalance'
  | 'canAccessPoChecker'
  | 'canAccessDoChecker'
  | 'canAccessGrChecker'
  | 'canAccessSkuChecker'
  | 'canAccessQohChecker'
  | 'canAccessPriceChecker'
  | 'canAccessPromotionChecker'
  | 'canAccessUserManagement'
  | 'canAccessEmailSettings'
  | 'canAccessBranchAdmin'
  | 'canAccessSyncTools'
  | 'canViewDailySalesDashboard'
  | 'canViewWeeklyCompanyReport'
  | 'canViewSalesSummary'
  | 'canViewMembershipDashboard'
  | 'canViewProductPerformanceDashboard'
  | 'canViewInventoryDashboard'
  | 'canViewDashboard'
  | 'canViewBranchFinancialPerformance'
  | 'canViewPromotionPerformanceDashboard';

export type PortalMenuItem = {
  path: string;
  name: string;
  icon?: ReactNode;
  access?: PortalMenuAccess;
  hideInMenu?: boolean;
  routes?: PortalMenuItem[];
};

export type PortalAccessMap = Record<string, boolean | undefined>;

export const MODULE_ENTRY_PATHS: Record<string, string> = {
  '/home': '/home/overview',
  '/dashboard': '/dashboard/overview',
  '/reports': '/reports/overview',
  '/tools': '/tools/overview',
  '/admin': '/admin/overview',
};

export const DAILY_SALES_DASHBOARD_MODULE_ID = 'daily_sales_dashboard';
export const WEEKLY_COMPANY_REPORT_MODULE_ID = 'weekly_company_report';
export const SALES_SUMMARY_MODULE_ID = 'sales_summary';
export const INVENTORY_DASHBOARD_MODULE_ID = 'inventory_dashboard';

export const BRANCH_DAILY_SALES_REPORT_ID = 'branch_daily_sales';
export const HOURLY_SALES_REPORT_ID = 'hourly_sales_report';
export const BRANCH_DAILY_SALES_COMPARE_REPORT_ID = 'branch_daily_sales_compare';
export const BRANCH_MONTHLY_SALES_REPORT_ID = 'branch_monthly_sales';
export const YEARLY_SALES_COMPARISON_REPORT_ID = 'yearly_sales_comparison';
export const FISCAL_WEEKLY_SALES_REPORT_ID = 'fiscal_weekly_sales_report';
export const SALES_BY_SKU_REPORT_ID = 'sales_by_sku';
export const SALES_BY_PROMOTION_REPORT_ID = 'sales_by_promotion';
export const COUPON_USAGE_REPORT_ID = 'coupon_usage';
export const VOUCHER_USAGE_REPORT_ID = 'voucher_usage_report';
export const PO_DETAILS_REPORT_ID = 'po_details';
export const GRN_DETAILS_REPORT_ID = 'grn_details';
export const DO_DETAILS_REPORT_ID = 'do_details';
export const STOCK_INVENTORY_REPORT_ID = 'stock_inventory';
export const STOCK_AGING_REPORT_ID = 'stock_aging';
export const STOCK_MOVEMENT_REPORT_ID = 'stock_movement';
export const BRANCH_STOCK_BALANCE_REPORT_ID = 'branch_stock_balance';
export const BRANCH_FINANCIAL_PERFORMANCE_REPORT_ID =
  'branch_financial_performance';
export const PROMOTION_PERFORMANCE_DASHBOARD_REPORT_ID =
  'promotion_performance_dashboard';

type MenuNode = {
  path?: string;
  children?: MenuNode[];
  routes?: MenuNode[];
};

export function normalizeAppPath(path: string): string {
  const pathPart = (path || '/').split('?')[0] || '/';
  return pathPart.replace(/\/$/, '') || '/';
}

function firstLeafPath(nodes: MenuNode[] | undefined): string | null {
  if (!nodes?.length) {
    return null;
  }
  for (const node of nodes) {
    const kids = node.children || node.routes;
    if (kids?.length) {
      const nested = firstLeafPath(kids);
      if (nested) {
        return nested;
      }
      continue;
    }
    if (node.path) {
      return normalizeAppPath(node.path);
    }
  }
  return null;
}

/**
 * Map a top-nav / parent path to the module overview (or first leaf fallback).
 */
export function resolveModuleEntryPath(
  path: string,
  menuItem?: MenuNode | null,
  _access?: PortalAccessMap,
): string {
  const normalized = normalizeAppPath(path);
  const mapped = MODULE_ENTRY_PATHS[normalized];
  if (mapped) {
    return mapped;
  }

  const fromChildren = firstLeafPath(menuItem?.children || menuItem?.routes);
  if (fromChildren) {
    return fromChildren;
  }

  return normalized;
}

function canSeeMenuItem(item: PortalMenuItem, access: PortalAccessMap): boolean {
  if (!item.access) {
    return true;
  }
  if (item.access === 'admin') {
    return !!access.canAdmin;
  }
  return !!access[item.access];
}

function filterMenuItems(items: PortalMenuItem[], access: PortalAccessMap): PortalMenuItem[] {
  return items
    .map((item) => {
      if (item.hideInMenu) {
        return null;
      }
      if (item.routes?.length) {
        const routes = filterMenuItems(item.routes, access);
        if (item.access && !canSeeMenuItem(item, access)) {
          return null;
        }
        if (routes.length === 0) {
          return null;
        }
        return { ...item, routes };
      }
      if (!canSeeMenuItem(item, access)) {
        return null;
      }
      return item;
    })
    .filter((item): item is PortalMenuItem => item !== null);
}

/** ProLayout route config — top-level modules + sidebar children. */
export function buildPortalMenu(access: PortalAccessMap): { path: string; routes: PortalMenuItem[] } {
  const routes: PortalMenuItem[] = [
    {
      path: '/home',
      name: 'Home',
      icon: <HomeOutlined />,
      routes: [
        {
          path: '/home/overview',
          name: 'Overview',
          hideInMenu: true,
        },
        {
          path: '/home/dashboard',
          name: 'System status',
          icon: <HomeOutlined />,
          hideInMenu: true,
        },
        {
          path: '/home/branch-info',
          name: 'Branch Information',
          icon: <ShopOutlined />,
        },
        {
          path: '/home/fiscal-calendar',
          name: 'Fiscal Calendar',
          icon: <CalendarOutlined />,
        },
      ],
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      icon: <DashboardOutlined />,
      access: 'canViewDashboard',
      routes: [
        {
          path: '/dashboard/overview',
          name: 'Overview',
          hideInMenu: true,
        },
        ...DASHBOARD_NAV.map((item) => ({
          path: item.path,
          name: item.name,
          icon: dashboardMenuIcon(item),
          access: item.access,
        })),
      ],
    },
    {
      path: '/reports',
      name: 'Reports',
      icon: <BarChartOutlined />,
      routes: [
        {
          path: '/reports/overview',
          name: 'Overview',
          hideInMenu: true,
        },
        {
          path: '/reports/branch-daily-sales',
          name: 'Branch Daily Sales',
          access: 'canViewBranchDailySales',
        },
        {
          path: '/reports/branch-daily-sales/pivot',
          name: 'Branch Daily Sales Pivot',
          access: 'canViewBranchDailySales',
          hideInMenu: true,
        },
        {
          path: '/reports/hourly-sales',
          name: 'Hourly Sales',
          access: 'canViewHourlySales',
        },
        {
          path: '/reports/hourly-sales/pivot',
          name: 'Hourly Sales Pivot',
          access: 'canViewHourlySales',
          hideInMenu: true,
        },
        {
          path: '/reports/branch-daily-sales-compare',
          name: 'Branch Daily Sales Compare',
          access: 'canViewBranchDailySalesCompare',
        },
        {
          path: '/reports/branch-monthly-sales',
          name: 'Branch Monthly Sales',
          access: 'canViewBranchMonthlySales',
        },
        {
          path: '/reports/yearly-sales-comparison',
          name: 'Yearly Sales Comparison',
          access: 'canViewYearlySalesComparison',
        },
        {
          path: '/reports/fiscal-weekly-sales',
          name: 'Fiscal Weekly Sales',
          access: 'canViewFiscalWeeklySales',
        },
        {
          path: '/reports/export-history',
          name: 'Export History',
        },
        {
          path: '/reports/sales-by-sku',
          name: 'Sales by SKU',
          access: 'canViewSalesBySku',
        },
        {
          path: '/reports/sales-by-sku/pivot',
          name: 'Sales by SKU Pivot',
          access: 'canViewSalesBySku',
          hideInMenu: true,
        },
        {
          path: '/reports/sales-by-promotion',
          name: 'Sales by Promotion',
          access: 'canViewSalesByPromotion',
        },
        {
          path: '/reports/coupon-usage',
          name: 'Coupon Usage',
          access: 'canViewCouponUsage',
        },
        {
          path: '/reports/coupon-usage/pivot',
          name: 'Coupon Usage Pivot',
          access: 'canViewCouponUsage',
          hideInMenu: true,
        },
        {
          path: '/reports/voucher-usage',
          name: 'Voucher Usage',
          access: 'canViewVoucherUsage',
        },
        {
          path: '/reports/voucher-usage/pivot',
          name: 'Voucher Usage Pivot',
          access: 'canViewVoucherUsage',
          hideInMenu: true,
        },
        {
          path: '/reports/po-details',
          name: 'PO Details Report',
          access: 'canViewPoDetails',
        },
        {
          path: '/reports/po-details/pivot',
          name: 'PO Details Pivot',
          access: 'canViewPoDetails',
          hideInMenu: true,
        },
        {
          path: '/reports/grn-details',
          name: 'Goods Receive Report',
          access: 'canViewGrnDetails',
        },
        {
          path: '/reports/grn-details/pivot',
          name: 'Goods Receive Pivot',
          access: 'canViewGrnDetails',
          hideInMenu: true,
        },
        {
          path: '/reports/do-details',
          name: 'Delivery Order Details',
          access: 'canViewDoDetails',
        },
        {
          path: '/reports/do-details/pivot',
          name: 'Delivery Order Pivot',
          access: 'canViewDoDetails',
          hideInMenu: true,
        },
        {
          path: '/reports/stock-inventory',
          name: 'Stock Inventory',
          access: 'canViewStockInventory',
        },
        {
          path: '/reports/stock-aging',
          name: 'Stock Aging',
          access: 'canViewStockAging',
        },
        {
          path: '/reports/stock-aging/pivot',
          name: 'Stock Aging Pivot',
          access: 'canViewStockAging',
          hideInMenu: true,
        },
        {
          path: '/reports/stock-movement',
          name: 'Stock Movement',
          access: 'canViewStockMovement',
        },
        {
          path: '/reports/branch-stock-balance',
          name: 'Branch Stock Balance',
          access: 'canViewBranchStockBalance',
        },
        {
          path: '/reports/branch-stock-balance/pivot',
          name: 'Branch Stock Balance Pivot',
          access: 'canViewBranchStockBalance',
          hideInMenu: true,
        },
      ],
    },
    {
      path: '/tools',
      name: 'Tools',
      icon: <ToolOutlined />,
      routes: [
        {
          path: '/tools/overview',
          name: 'Overview',
          hideInMenu: true,
        },
        {
          path: '/tools/po-checker',
          name: 'PO Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessPoChecker',
        },
        {
          path: '/tools/do-checker',
          name: 'DO Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessDoChecker',
        },
        {
          path: '/tools/gr-checker',
          name: 'GR Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessGrChecker',
        },
        {
          path: '/tools/sku-checker',
          name: 'SKU Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessSkuChecker',
        },
        {
          path: '/tools/qoh-checker',
          name: 'QOH Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessQohChecker',
        },
        {
          path: '/tools/price-checker',
          name: 'Price Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessPriceChecker',
        },
        {
          path: '/tools/promotion-checker',
          name: 'Promotion Checker',
          icon: <FileSearchOutlined />,
          access: 'canAccessPromotionChecker',
        },
      ],
    },
  ];

  if (access.canAdmin) {
    routes.push({
      path: '/admin',
      name: 'Administration',
      icon: <TeamOutlined />,
      access: 'admin',
      routes: [
        {
          path: '/admin/overview',
          name: 'Overview',
          access: 'admin',
          hideInMenu: true,
        },
        {
          path: '/admin/appearance',
          name: 'Appearance',
          icon: <BgColorsOutlined />,
          access: 'admin',
        },
        {
          path: '/admin/settings/email',
          name: 'Email',
          icon: <MailOutlined />,
          access: 'canAccessEmailSettings',
        },
        {
          path: '/admin/settings/session',
          name: 'Session',
          icon: <ClockCircleOutlined />,
          access: 'admin',
        },
        {
          path: '/admin/settings/general',
          name: 'General',
          icon: <CalendarOutlined />,
          access: 'admin',
        },
        {
          path: '/admin/branches',
          name: 'Branches',
          icon: <ShopOutlined />,
          access: 'canAccessBranchAdmin',
        },
        {
          path: '/admin/sync-tools',
          name: 'Sync tools',
          icon: <SyncOutlined />,
          access: 'canAccessSyncTools',
        },
        {
          path: '/admin/users',
          name: 'Users',
          icon: <UserOutlined />,
          access: 'canAccessUserManagement',
        },
        {
          path: '/admin/users/import',
          name: 'Import Users',
          access: 'canAccessUserManagement',
          hideInMenu: true,
        },
      ],
    });
  }

  const filtered = filterMenuItems(routes, access);

  return { path: '/', routes: filtered };
}
